import { Box, Grow, Stack } from '@mui/material'
import isEmpty from 'lodash-es/isEmpty'
import { useEffect, useState } from 'react'
import { Color } from '../../../chess/types'
import useDescriptionText from '../../PuzzlesView/hooks/useDescriptionText'
import getFeedbackMessage, { FeedbackType } from '../helpers/getFeedbackMessage'
import BubbleContent from './BubbleContent'

const FEEDBACK_DURATION = 7000 // 7 seconds

type CharacterProps = {
    avatar: string
    message: string
    myColor: Color
    movesCount: number
    feedbackType?: FeedbackType
    clearFeedbackType: () => void
}

const Character: React.FC<CharacterProps> = ({
    avatar,
    message,
    myColor,
    movesCount,
    feedbackType,
    clearFeedbackType,
}) => {
    const [displayMessage, setDisplayMessage] = useState<string>('')

    // Update description message when props change
    useEffect(() => {
        // Only update description if not showing feedback
        if (feedbackType) return

        const result = useDescriptionText(message, {
            opponentColor: myColor === Color.Black ? 'White' : 'Black',
            myColor: myColor === Color.Black ? 'Black' : 'White',
            movesCount: movesCount,
        })
        setDisplayMessage(result)
    }, [movesCount, message, myColor, feedbackType])

    // Handle feedback events
    useEffect(() => {
        if (!feedbackType) return

        // Get random feedback message
        const feedbackMessage = getFeedbackMessage(feedbackType)
        setDisplayMessage(feedbackMessage)

        // For puzzle solved, don't return to description
        if (feedbackType === FeedbackType.PUZZLE_SOLVED) {
            return
        }

        // Set timeout to return to description after FEEDBACK_DURATION
        const timeout = setTimeout(() => {
            clearFeedbackType()
        }, FEEDBACK_DURATION)

        // Cleanup timeout on unmount or when effect re-runs
        return () => {
            clearTimeout(timeout)
        }
    }, [feedbackType])

    return (
        <Stack
            direction="row"
            sx={{
                alignSelf: 'stretch',
                flexGrow: 0,
                position: 'relative',
                zIndex: 1,
                marginBottom: { xs: '-20px', md: '-10px' },
            }}
        >
            {/* Avatar */}
            <Box
                sx={{
                    display: 'flex',
                    flexShrink: 0,
                    width: 103,
                    height: 103,
                }}
            >
                <img src={avatar} alt="Character" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>

            {/* Bubble */}
            <Grow
                in={!isEmpty(displayMessage)}
                key={displayMessage}
                timeout={500}
                style={{ transformOrigin: 'left bottom' }}
            >
                <Stack
                    sx={(theme) => ({
                        position: 'absolute',
                        left: '103px', // 103px (avatar)
                        bottom: 5,
                        right: 0,
                        backgroundColor: theme.palette.common.white,
                        color: theme.palette.common.black,
                        boxShadow: theme.shadows[4],
                        fontSize: 14,
                        border: `1px solid ${theme.palette.common.white}`,
                        borderRadius: '8px',
                        padding: '8px',
                        marginBottom: '10px',
                        zIndex: 10,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: '-12px',
                            bottom: '0px',
                            transform: 'translateY(-50%)',
                            width: 0,
                            height: 0,
                            borderTop: 0,
                            borderBottom: '12px solid transparent',
                            borderRight: `12px solid ${theme.palette.common.white}`,
                        },
                    })}
                >
                    <BubbleContent message={displayMessage} />
                </Stack>
            </Grow>
        </Stack>
    )
}

export default Character
